import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'

export const LOCAL_STORAGE_SCOPE = {
  GLOBAL: 'global',
  PAGE: 'page',
  ROUTE: 'route',
} as const
export type LocalStorageScope = (typeof LOCAL_STORAGE_SCOPE)[keyof typeof LOCAL_STORAGE_SCOPE]

interface PersistenceConfig<T> {
  key: string
  defaultValue: T
  scope?: LocalStorageScope
}

/**
 * Generic hook for persisting data in localStorage with different scoping options.
 *
 * @param config Configuration object with key, default value, and optional scope
 * @returns Object with persisted state and update function
 */
export function useLocalStoragePersistence<T>({
  key,
  defaultValue,
  scope = LOCAL_STORAGE_SCOPE.PAGE,
}: PersistenceConfig<T>) {
  const location = useLocation()

  // Generate storage key based on scope
  const storageKey = useMemo(() => {
    const baseKey = `cmoh-${key}`

    switch (scope) {
      case LOCAL_STORAGE_SCOPE.GLOBAL:
        return baseKey
      case LOCAL_STORAGE_SCOPE.PAGE:
        return `${baseKey}-${location.pathname.split('/')[1]}`
      case LOCAL_STORAGE_SCOPE.ROUTE:
        return `${baseKey}-${location.pathname}`
      default:
        return baseKey
    }
  }, [key, scope, location.pathname])

  // Load initial state from localStorage
  const [state, setState] = useState<T>(() => {
    try {
      const stored = localStorage.getItem(storageKey)
      if (stored) {
        return JSON.parse(stored) as T
      }
    } catch (error) {
      console.warn(`Failed to load persisted data for ${key}:`, error)
    }

    return defaultValue
  })

  // Persist state changes to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(state))
    } catch (error) {
      console.warn(`Failed to save persisted data for ${key}:`, error)
    }
  }, [state, storageKey, key])

  // Update function
  const updateState = useCallback((newState: T | ((prev: T) => T)) => {
    setState((prev) => {
      const nextState =
        typeof newState === 'function' ? (newState as (prev: T) => T)(prev) : newState
      return nextState
    })
  }, [])

  // Reset to default
  const resetToDefault = useCallback(() => {
    setState(defaultValue)
  }, [defaultValue])

  return {
    state,
    updateState,
    resetToDefault,
  }
}
